// §4.2 tabs — History · Genealogy · Reservations.
//
// History  : GET /stock-units/{id}/history (movements ⊕ scan events, newest
//            first, cursor-paged). The cost column obeys the §7 presence rule
//            in lib/cost.ts: rendered only when the page holds a visible value,
//            otherwise replaced by an honest one-line note.
// Genealogy: THE SHAPE OF THIS TAB WAS WRONG AND IS NOW EXACT (see the header
//            of `GenealogyTab`): the lot trace is LOT-rooted, so filtering it to
//            "edges incident on this unit" provably yielded the empty set for
//            the one lineage that matters in a cable factory — the cut. The tab
//            now reads the unit's own `parent_stock_unit_id` for the piece it
//            was cut from, and finds its children by their `parent_stock_unit_id`
//            through §5 row 12's own filter (D-212) — never through the lot,
//            whose id a §3.2-drained parent no longer carries. The lot-level
//            edges are still shown, from the lot trace, when the unit has one.
// Reserv.  : the unit card carries `active_reservation`; the full per-unit
//            history comes from GET /reservations?product_id= filtered to this
//            unit (there is no stock_unit_id filter on §5 row 28). The endpoint
//            is gated on `inventory.reservation.manage` (§5 row 28), so a qc or
//            finance caller is told the holds are not theirs to see instead of
//            being shown a raw 403.

import { enumLabel } from "../../../../../lib/enum-label";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { DataTable } from "../../../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../../components/states";
import { requestIdOf } from "../../../../../lib/request";
import { CursorPager } from "../../components/filters";
import {
  InventoryStatus,
  MoneyCell,
  MoneyColumnNote,
  RefLabel,
} from "../../components/inventory-ui";
import {
  useLotTrace,
  usePrincipalIndex,
  useReleaseReservation,
  useReservations,
  useStockUnit,
  useStockUnitHistory,
  useStockUnits,
  type StockUnit,
  type TraceEdge,
  type TraceNode,
} from "../../hooks/inventory-queries";
import { useInventoryPermissions } from "../../hooks/permissions";
import { costPresence, showCostColumn } from "../../lib/cost";
import { inventoryErrorMessage } from "../../lib/errors";
import { formatTimestamp, shortId, formatMeasureWithUom } from "../../lib/measure";

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export function HistoryTab({ unit }: { unit: StockUnit }) {
  const { t, i18n } = useTranslation();
  const perms = useInventoryPermissions();
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const history = useStockUnitHistory(unit.id, cursor);
  const principals = usePrincipalIndex(perms.canReadMembers);

  if (history.isPending) {
    return <TableSkeleton rows={5} cols={4} label={t("inventory.unitCard.historyLoading")} />;
  }
  if (history.error) {
    return (
      <ErrorState
        title={t("inventory.unitCard.historyError")}
        detail={history.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void history.refetch()}
        requestId={requestIdOf(history.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }

  const items = history.data.items;
  type HistoryItem = (typeof items)[number];
  if (items.length === 0) {
    return (
      <EmptyState
        title={t("inventory.unitCard.historyEmptyTitle")}
        body={t("inventory.unitCard.historyEmptyBody")}
      />
    );
  }

  const movements = items.flatMap((item) => (item.kind === "movement" ? [item.movement] : []));
  const presence = costPresence(movements, (row) => row.unit_cost_minor);
  const withCost = showCostColumn(presence);

  return (
    <>
      <MoneyColumnNote presence={presence} />
      <DataTable
        columns={[
          {
            key: "when",
            header: t("inventory.unitCard.colWhen"),
            id: true,
            render: (item) =>
              item.kind === "movement"
                ? formatTimestamp(item.movement.moved_at, i18n.language)
                : formatTimestamp(item.scan.occurred_at, i18n.language),
          },
          {
            key: "event",
            header: t("inventory.unitCard.colEvent"),
            render: (item) =>
              item.kind === "movement"
                ? enumLabel(t, "inventory.vocab.movement", item.movement.movement_type)
                : enumLabel(t, "inventory.vocab.scan", item.scan.action),
          },
          {
            key: "quantity",
            header: t("inventory.unitCard.colQuantity"),
            numeric: true,
            // A SCAN moves nothing (§4.2) — an em dash is the true answer, and
            // a blank cell would read as a load failure.
            render: (item) =>
              item.kind === "movement"
                ? formatMeasureWithUom(item.movement.quantity, item.movement.uom, {
                    locale: i18n.language,
                  })
                : "—",
          },
          // §7 redaction-by-absence: no column at all without the key.
          ...(withCost
            ? [
                {
                  key: "unit_cost_minor",
                  header: t("inventory.money.unitCost"),
                  numeric: true,
                  render: (item: HistoryItem) =>
                    item.kind === "movement" ? (
                      <MoneyCell
                        row={item.movement}
                        valueOf={(row) => row.unit_cost_minor}
                        currencyOf={(row) => row.currency}
                      />
                    ) : (
                      "—"
                    ),
                },
              ]
            : []),
          {
            key: "note",
            header: t("inventory.unitCard.colNote"),
            render: (item) =>
              item.kind === "movement" ? (
                (item.movement.note ?? "")
              ) : (
                <>
                  {t("inventory.unitCard.scanBy", { who: "" })}{" "}
                  <RefLabel
                    value={principals.byId.get(item.scan.performed_by)}
                    id={item.scan.performed_by}
                    pending={principals.isPending}
                    kind="user"
                  />
                </>
              ),
          },
        ]}
        rows={items}
        rowKey={(item) =>
          item.kind === "movement" ? `m-${item.movement.id}` : `s-${item.scan.id}`
        }
        empty={null}
        rowClassName={(item) => (item.kind === "scan" ? "row-scan" : undefined)}
      />
      <CursorPager
        canPrev={cursors.length > 1}
        nextCursor={history.data.next_cursor}
        onPrev={() => {
          setCursors((prev) => prev.slice(0, -1));
        }}
        onNext={(next) => {
          setCursors((prev) => [...prev, next]);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Genealogy
// ---------------------------------------------------------------------------

/**
 * §4.2 Genealogy — parents above, children below, each a tap-through.
 *
 * WHY IT IS NOT JUST THE LOT TRACE. `GET /lots/{id}/trace` anchors on
 * `parent_kind='lot' AND parent_id=$lotId` (`genealogy.repo.ts` walkClosure),
 * receipts write `childKind:'lot'` and cuts write `stock_unit → stock_unit`.
 * A unit is therefore never a child of its own lot in the DAG, so filtering
 * that trace to "edges incident on this unit" returned nothing in every real
 * case — including the cut lineage M06-S09 traces. The exact facts are on the
 * unit rows themselves:
 *   parent   → `unit.parent_stock_unit_id` (the piece this was cut from)
 *   children → the units whose `parent_stock_unit_id` is this unit, via §5
 *              row 12's `parent_stock_unit_id` filter (D-212) — never guessed
 *              from a serial prefix, and NEVER keyed on this unit's lot.
 * The lot-level edges the trace DOES carry for this unit (a consumed-into, a
 * repack) are still listed, from the lot trace, when the unit carries a lot.
 *
 * WHY THE CHILD READ IS PARENT-KEYED (defect #122, D-212). It used to be §5
 * row 12's `lot_id` filter ("cut children inherit the parent's lot"), which is
 * true at cut time and false one step later: §3.2 step 4's exhaustion flip
 * NULLs a drained parent's `lot_id`, so the exact unit a recall walks through
 * (M06-S08/S09 — the bobbin cut to exhaustion) was the one whose children the
 * tab could not list, at any depth of the cut chain. The parent filter reads
 * the DAG fact itself and is indifferent to the parent's terminal status.
 */
export function GenealogyTab({ unit, canTrace }: { unit: StockUnit; canTrace: boolean }) {
  const { t } = useTranslation();
  const perms = useInventoryPermissions();
  const lotId = unit.lot_id ?? "";

  // A custom tenant role resolves to zero client-side grants by design, so the
  // mirror may say "no trace" for a role the SERVER would allow. The read is
  // therefore issued whenever the mirror is not sure, and a real 403 is
  // rendered from the server's own problem copy (the mirror still governs
  // CONTROLS everywhere else).
  const mirrorRefuses = canTrace ? false : perms.isResolved && !perms.isFeedError;
  const traceEnabled = !mirrorRefuses && lotId.length > 0;
  const up = useLotTrace(lotId, "up", traceEnabled);
  const down = useLotTrace(lotId, "down", traceEnabled);

  const parentId = unit.parent_stock_unit_id;
  const parent = useStockUnit(parentId ?? "");
  // Parent-keyed, deliberately NOT lot-keyed (D-212): a drained parent has no
  // lot_id, and its children are exactly what a recall needs listed.
  const childQuery = useStockUnits({ parent_stock_unit_id: unit.id, limit: 200 });
  const children = childQuery.data?.items ?? [];
  const childrenTruncated = (childQuery.data?.next_cursor ?? null) !== null;

  if (mirrorRefuses && lotId.length > 0) {
    return <p className="inv-hint">{t("inventory.unitCard.genealogyNoPermission")}</p>;
  }
  if (up.error || down.error) {
    const error = up.error ?? down.error;
    return (
      <ErrorState
        title={t("inventory.unitCard.genealogyError")}
        detail={error?.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => {
          void up.refetch();
          void down.refetch();
        }}
        requestId={requestIdOf(error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }
  if (traceEnabled && (up.isPending || down.isPending)) {
    return <TableSkeleton rows={3} cols={3} label={t("inventory.unitCard.genealogyLoading")} />;
  }

  const nodes = new Map<string, TraceNode>();
  for (const node of [...(up.data?.nodes ?? []), ...(down.data?.nodes ?? [])]) {
    nodes.set(node.id, node);
  }
  // Edges of the lot trace that name THIS unit on either side — real lineage
  // (a consumption, a repack), just not the cut relation.
  const lotEdges = [...(up.data?.edges ?? []), ...(down.data?.edges ?? [])].filter(
    (edge) =>
      (edge.parent_kind === "stock_unit" && edge.parent_id === unit.id) ||
      (edge.child_kind === "stock_unit" && edge.child_id === unit.id),
  );
  const traceTruncated = (up.data?.truncated ?? false) || (down.data?.truncated ?? false);

  const nothing = parentId === null && children.length === 0 && lotEdges.length === 0;

  return (
    <div className="genealogy" data-testid="unit-genealogy">
      {traceTruncated ? (
        <p className="degraded-note" role="note">
          {t("inventory.trace.truncated")}
        </p>
      ) : null}

      <div className="genealogy-side">
        <h3>{t("inventory.unitCard.parents")}</h3>
        {parentId === null ? (
          <p className="inv-hint">{t("inventory.unitCard.noneRecorded")}</p>
        ) : (
          <ul className="inv-list" data-testid="genealogy-parents">
            <li data-testid="genealogy-parent">
              <Link to="/inventory/units/$id" params={{ id: parentId }}>
                <RefLabel
                  value={parent.data?.unit.serial_code}
                  id={parentId}
                  pending={parent.isPending}
                  kind="stock_unit"
                />
              </Link>{" "}
              <span className="rel-label">{t("inventory.vocab.relation.cut_from")}</span>
            </li>
          </ul>
        )}
      </div>

      <div className="genealogy-self">
        <strong>{unit.serial_code}</strong>
      </div>

      <div className="genealogy-side">
        <h3>{t("inventory.unitCard.children")}</h3>
        {childQuery.isPending ? (
          <TableSkeleton rows={2} cols={2} label={t("inventory.unitCard.genealogyLoading")} />
        ) : childQuery.error ? (
          <ErrorState
            title={t("inventory.unitCard.genealogyError")}
            detail={childQuery.error.message}
            retryLabel={t("inventory.common.retry")}
            onRetry={() => void childQuery.refetch()}
            requestId={requestIdOf(childQuery.error)}
            requestIdLabel={t("inventory.common.requestId")}
          />
        ) : children.length === 0 ? (
          <p className="inv-hint">{t("inventory.unitCard.noneRecorded")}</p>
        ) : (
          <>
            {childrenTruncated ? (
              <p className="degraded-note" role="note">
                {t("inventory.unitCard.childrenTruncated")}
              </p>
            ) : null}
            <ul className="inv-list" data-testid="genealogy-children">
              {children.map((child) => (
                <li
                  key={child.id}
                  data-testid="genealogy-child"
                  data-serial={child.serial_code}
                  data-remaining={child.remaining_measure ?? ""}
                  data-offcut={String(child.is_offcut)}
                >
                  <Link to="/inventory/units/$id" params={{ id: child.id }}>
                    {child.serial_code}
                  </Link>{" "}
                  <span className="rel-label">{t("inventory.vocab.relation.cut_from")}</span>{" "}
                  <span className="rel-qty">
                    {formatMeasureWithUom(child.remaining_measure, child.measure_uom, {
                      locale: undefined,
                    })}
                  </span>
                  {child.is_offcut ? (
                    <span className="rel-label"> {t("inventory.cut.offcut")}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {lotEdges.length > 0 ? (
        <EdgeList
          title={t("inventory.unitCard.lotEdges")}
          edges={lotEdges}
          nodes={nodes}
          unitId={unit.id}
        />
      ) : null}

      {nothing ? <p className="inv-hint">{t("inventory.unitCard.genealogyEmptyBody")}</p> : null}

      {lotId.length > 0 && !mirrorRefuses ? (
        <p className="inv-hint">
          <Link to="/inventory/lots/$id/trace" params={{ id: lotId }}>
            {t("inventory.unitCard.openLotTrace")}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Lot-level edges naming this unit. The OTHER end of the edge is the one worth
 * linking: on an edge where the unit is the parent, that is the child, and vice
 * versa — the inversion this list used to have pointed every row back at the
 * unit itself.
 */
function EdgeList({
  title,
  edges,
  nodes,
  unitId,
}: {
  title: string;
  edges: readonly TraceEdge[];
  nodes: ReadonlyMap<string, TraceNode>;
  unitId: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <div className="genealogy-side">
      <h3>{title}</h3>
      <ul className="inv-list">
        {edges.map((edge) => {
          const unitIsParent = edge.parent_kind === "stock_unit" && edge.parent_id === unitId;
          const otherKind = unitIsParent ? edge.child_kind : edge.parent_kind;
          const otherId = unitIsParent ? edge.child_id : edge.parent_id;
          const node = nodes.get(otherId);
          return (
            <li key={`${edge.parent_id}-${edge.child_id}-${edge.relation}`}>
              {otherKind === "lot" ? (
                <Link to="/inventory/lots/$id" params={{ id: otherId }}>
                  {node?.label ?? shortId(otherId)}
                </Link>
              ) : (
                <Link to="/inventory/units/$id" params={{ id: otherId }}>
                  {node?.label ?? shortId(otherId)}
                </Link>
              )}{" "}
              <span className="rel-label">
                {enumLabel(t, "inventory.vocab.relation", edge.relation)}
              </span>{" "}
              <span className="rel-direction">
                {unitIsParent
                  ? t("inventory.unitCard.edgeDownstream")
                  : t("inventory.unitCard.edgeUpstream")}
              </span>{" "}
              <span className="rel-qty">
                {formatMeasureWithUom(edge.quantity, edge.uom, { locale: i18n.language })}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reservations
// ---------------------------------------------------------------------------

export function ReservationsTab({ unit }: { unit: StockUnit }) {
  const { t, i18n } = useTranslation();
  const perms = useInventoryPermissions();
  const productId = unit.product_id ?? "";
  // §5 row 28 gates GET /reservations on `inventory.reservation.manage`
  // (planner/sales/procurement/storekeeper/admins). qc, finance, maintenance
  // and floor_worker hold `inventory.read` and would get a raw 403 panel; the
  // mirror is only trusted to REFUSE when it actually resolved a system role.
  const mirrorRefuses =
    !perms.has("inventory.reservation.manage") && perms.isResolved && !perms.isFeedError;
  const reservations = useReservations(
    { product_id: productId, limit: 200 },
    !mirrorRefuses && productId.length > 0,
  );
  const release = useReleaseReservation();
  const [confirming, setConfirming] = useState<string | null>(null);

  if (mirrorRefuses) {
    return <p className="inv-hint">{t("inventory.unitCard.reservationsNoPermission")}</p>;
  }
  if (productId.length === 0) {
    return (
      <EmptyState
        title={t("inventory.unitCard.reservationsBareTitle")}
        body={t("inventory.unitCard.reservationsBareBody")}
      />
    );
  }
  if (reservations.isPending) {
    return <TableSkeleton rows={3} cols={4} label={t("inventory.unitCard.reservationsLoading")} />;
  }
  if (reservations.error) {
    return (
      <ErrorState
        title={t("inventory.unitCard.reservationsError")}
        detail={reservations.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void reservations.refetch()}
        requestId={requestIdOf(reservations.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }

  const rows = reservations.data.items.filter((row) => row.stock_unit_id === unit.id);
  const truncated = reservations.data.next_cursor !== null;

  if (rows.length === 0) {
    return (
      <EmptyState
        title={t("inventory.unitCard.reservationsEmptyTitle")}
        body={t("inventory.unitCard.reservationsEmptyBody")}
      />
    );
  }

  return (
    <>
      {truncated ? (
        <p className="degraded-note" role="note">
          {t("inventory.unitCard.reservationsTruncated")}
        </p>
      ) : null}
      {release.isError ? (
        <p className="form-error" role="alert">
          {inventoryErrorMessage(t, release.error, "inventory.unitCard.releaseHoldFailed")}
        </p>
      ) : null}
      {release.isSuccess ? (
        <p className="inv-success" role="status">
          {t("inventory.unitCard.holdReleased")}
        </p>
      ) : null}
      <DataTable
        columns={[
          {
            key: "demand",
            header: t("inventory.unitCard.colDemand"),
            id: true,
            render: (row) => (
              <>
                {enumLabel(t, "inventory.reserve.kind", row.demand_type)}{" "}
                {/* The WORDS where the hold has them (D-151) — a storekeeper
                    reads "BuildCo · Thursday", not eight hex characters. The
                    short id stays for the module-minted kinds, which carry no
                    reference at all. */}
                {row.demand_ref !== null ? (
                  <span className="demand-ref">{row.demand_ref}</span>
                ) : (
                  <code>{shortId(row.demand_id)}</code>
                )}
              </>
            ),
          },
          {
            key: "quantity",
            header: t("inventory.unitCard.colQuantity"),
            numeric: true,
            render: (row) => formatMeasureWithUom(row.quantity, row.uom, { locale: i18n.language }),
          },
          {
            key: "status",
            header: t("inventory.unitCard.colStatus"),
            render: (row) => <InventoryStatus status={row.status} scope="status" />,
          },
          {
            key: "created_at",
            header: t("inventory.unitCard.colCreated"),
            age: true,
            render: (row) => formatTimestamp(row.created_at, i18n.language),
          },
          {
            key: "actions",
            header: t("inventory.countReview.colActions"),
            actions: true,
            render: (row) =>
              row.status !== "active" ? (
                "—"
              ) : confirming === row.id ? (
                <span className="inline-confirm">
                  <span className="inv-warning">
                    {t("inventory.unitCard.releaseHoldConfirm", {
                      demand: enumLabel(t, "inventory.reserve.kind", row.demand_type),
                      // Confirming a release must name what is being broken in
                      // the words it was promised under (D-151).
                      id: row.demand_ref ?? shortId(row.demand_id),
                    })}
                  </span>
                  <Button
                    busy={release.isPending}
                    onClick={() => {
                      release.mutate({ id: row.id });
                      setConfirming(null);
                    }}
                  >
                    {t("inventory.common.confirm")}
                  </Button>
                  <Button
                    onClick={() => {
                      setConfirming(null);
                    }}
                  >
                    {t("inventory.common.cancel")}
                  </Button>
                </span>
              ) : (
                <Button
                  onClick={() => {
                    setConfirming(row.id);
                  }}
                >
                  {t("inventory.unitCard.releaseHold")}
                </Button>
              ),
          },
        ]}
        rows={rows}
        rowKey={(row) => row.id}
        empty={null}
      />
    </>
  );
}
